import { defaultHandleUserInteractionClick, isMobileUserAgent } from '@sportal365/smp-widget-utils';
import { useState } from 'react';
import { USER_INTERACTION } from '../../../../constants/entityTypes';
import { DROPDOWN_SELECTION_SUBSCRIBE_TYPE } from '../../../../constants/subscribeTypes';
import useDetectClickOutside from '../../../../hooks/useDetectClickOutside';
import { ISubscriberInfo } from '../../../../types';
import { SMPContainer } from '../../atoms/SMPContainer';
import { SMPFlex } from '../../atoms/SMPFlex';
import { SMPText } from '../../atoms/SMPText';
import { SMPArrowDownIcon } from '../../atoms/icons/SMPArrowDownIcon';
import { MobileModal } from '../MobileModal';

type DropdownOption = {
  label?: string;
  value?: number | string;
};

export type DropdownOptionsProps = {
  options?: DropdownOption[];
  value?: string;
  onChange?: (value?: string) => void;
  handleSubscriptionClick?: (info: ISubscriberInfo, clickType: string) => void;
  strictValueCheck?: boolean;
  parentRef?: React.RefObject<HTMLDivElement | null>;
};

export type SelectOptionsProps = {
  options: DropdownOption[];
  selectedOption: DropdownOption | undefined;
  handleDateClickAndSendInteractionEvent: (optionValue: string) => void;
};

export const Dropdown = ({
  value = '',
  options = [],
  onChange,
  strictValueCheck = false,
  handleSubscriptionClick,
  parentRef,
}: DropdownOptionsProps) => {
  const selectedOption = options.find((option: DropdownOption) =>
    strictValueCheck ? option?.value?.toString() === value : option?.value?.toString().includes(value),
  );

  const { isComponentVisible, setIsComponentVisible, ref } = useDetectClickOutside<HTMLDivElement>();
  const isMobile = isMobileUserAgent();

  const toggleShowDropdown = () => {
    setIsComponentVisible((prevValue) => !prevValue);
  };

  const handleDateClickAndSendInteractionEvent = (optionValue: string) => {
    onChange && value !== optionValue && onChange(optionValue);
    setIsComponentVisible(false);
    handleSubscriptionClick &&
      defaultHandleUserInteractionClick(handleSubscriptionClick, USER_INTERACTION, DROPDOWN_SELECTION_SUBSCRIBE_TYPE);
  };

  return (
    <SMPContainer
      className='dropdown-container'
      ref={ref}
      style={{
        margin: 0,
        padding: 0,
        borderRadius: '6px',
        position: 'relative',
        width:
          !(
            ref.current?.parentElement?.classList.contains('c-MarketSelectionWrapper-OddsDisplay') &&
            ref.current.parentElement.classList.contains('with-values-selection')
          ) &&
          parentRef?.current &&
          parentRef.current.clientWidth < 640
            ? '100%'
            : undefined,
      }}
    >
      <SMPFlex
        align={'center'}
        justify={'spaceBetween'}
        onClick={toggleShowDropdown}
        style={{
          position: 'relative',
          padding: '9px 12px',
          color: 'var(--colors-hiContrast)',
          cursor: 'pointer',
          fontWeight: 'bold',
          fontFamily: 'var(--fonts-primaryFontFamily)',
          lineHeight: '20px',
          border: '1px solid',
          backgroundColor: 'var(--colors-pillBackgroundColor)',
          borderRadius: '6px',
          borderColor: isComponentVisible ? 'var(--colors-orange500)' : 'var(--colors-dropdownBorderColor)',
          gap: '8px',
        }}
      >
        <SMPText
          style={{
            height: '100%',
            maxWidth: '85%',
            color: 'var(--colors-hiContrast)',
            fontWeight: 'bold',
            fontFamily: 'var(--fonts-primaryFontFamily)',
            fontSize: '14px',
            lineHeight: '20px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {selectedOption?.label}
        </SMPText>
        <SMPArrowDownIcon
          style={{
            color: isComponentVisible ? 'var(--colors-orange500)' : 'var(--colors-selectButtonSecondaryColor)',
            transform: isComponentVisible ? 'rotate(180deg)' : 'unset',
          }}
        />
      </SMPFlex>
      {!isMobile ? (
        <SMPContainer
          style={{
            margin: 0,
            padding: 0,
            display: isComponentVisible !== false ? 'block' : 'none',
            width: parentRef && parentRef.current?.clientWidth ? parentRef.current?.clientWidth * 0.5 : 320,
            maxWidth: 320,
            maxHeight: '250px',
            position: 'absolute',
            boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.10)',
            borderRadius: '6px',
            overflow: 'auto',
            top: 'calc(100% + 2px)',
            right: 0,
            zIndex: 1000,
          }}
        >
          <DropdownOptions
            options={options}
            selectedOption={selectedOption}
            handleDateClickAndSendInteractionEvent={handleDateClickAndSendInteractionEvent}
          />
        </SMPContainer>
      ) : (
        <MobileModal isOpen={isComponentVisible !== false} onClose={() => setIsComponentVisible(false)}>
          <DropdownOptions
            options={options}
            selectedOption={selectedOption}
            handleDateClickAndSendInteractionEvent={handleDateClickAndSendInteractionEvent}
          />
        </MobileModal>
      )}
    </SMPContainer>
  );
};

const DropdownOptions = ({ options, selectedOption, handleDateClickAndSendInteractionEvent }: SelectOptionsProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number>();

  return (
    <>
      {options.map((option, index) => {
        const activeOption = selectedOption?.value == option.value;
        return (
          <SMPContainer
            key={option.value}
            style={{
              margin: 0,
              backgroundColor:
                activeOption || index === hoveredIndex ? 'var(--colors-dropdownActiveBgColor)' : 'var(--colors-dropdownBtnColor)',
              color: activeOption || index === hoveredIndex ? 'var(--colors-orange500)' : 'var(--colors-hiContrast)',
              fontSize: '14px',
              padding: '16px',
              textAlign: 'left',
              fontWeight: 'bold',
              fontFamily: 'var(--fonts-primaryFontFamily)',
              lineHeight: '20px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              cursor: 'pointer',
              border: 'none',
              borderBottom: '1px solid',
              borderColor: 'var(--colors-selectOptionBorderColor)',
              outline: 'none',
              width: '100%',
            }}
            onMouseOver={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(undefined)}
            onClick={() => handleDateClickAndSendInteractionEvent(option?.value as string)}
          >
            {option.label}
          </SMPContainer>
        );
      })}
    </>
  );
};
